import React from 'react';
import {
  Action, ActionIcon, Card, ConfirmModal, DetailItemList, FormHandlers, Grid, HStack, ICON_CHECK, ICON_CLOSE, ICON_PENCIL_OUTLINE, ICON_TRASH_CAN_OUTLINE, Message, Portal, Spacing, Stack, Text,
  useModal,
  useNotifications,
  SimpleTable,
  SimpleTableSkeleton,
} from '@doveit/bricks';
import { formatFloor, formatSquareMeters } from '@doveit/hammer';
import { Property, Garage } from '../../../../providers/api/dtos';
import useGarages from '../../../hooks/use-garages/useGarages';
import useIsDevice from '../../../../hooks/use-is-device/useIsDevice';

import { NO_VALUE_SYMBOL } from '../../../utils';
import UpsertPropertyGarageForm, { UpsertPropertyGarageFormModel } from '../../components/upsert-property-garage-form/UpsertPropertyGarageForm';
import Divider from '../../../../components/divider/Divider';
import { GarageType } from '../../../../domain/types/garageType';
import { createGarage, deleteGarage, updateGarage } from '../../../../providers/api/garage/garageProvider';
import { garageTypeLabels } from '../../../../labels/garageTypeLabels';

export interface ManagePropertyGaragesProps {
  propertyId: NonNullable<Property['id']>,
  onCreate?: (createdGarage: Garage) => void,
  onUpdate?: (updatedGarage: Garage) => void,
  onDelete?: (deletedGarageId: NonNullable<Garage['id']>) => void,
}

const ManagePropertyGarages: React.FC<ManagePropertyGaragesProps> = ({
  propertyId,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const isMobile = useIsDevice('mobile');
  const [isSaving, setIsSaving] = React.useState(false);
  const { addSuccess, addError } = useNotifications();
  const [isEditing, setIsEditing] = React.useState<{ garage: Garage } | false>(false);
  const createGarageFormRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const updateGarageFormRefs = React.useRef<{ [key: number]: React.MutableRefObject<any> }>({});
  const deleteGarageModal = useModal<{ garage: Garage }>();

  const {
    data: garages,
    isLoading: areGaragesLoading,
    error: garagesErrors,
    mutate: mutateGarages,
  } = useGarages(propertyId);

  const addGarage = React.useCallback(async (formValues: UpsertPropertyGarageFormModel) => {
    try {
      setIsSaving(true);

      const garageToCreate: Garage = {
        id: null,
        propertyId,
        type: formValues.type as GarageType,
        adjacent: formValues.adjacent,
        size: formValues.size ? parseFloat(formValues.size) : null,
        floor: formValues.floor ? parseFloat(formValues.floor) : null,
      };

      const createdGarage = await createGarage(garageToCreate);

      setIsSaving(false);
      addSuccess('Lo spazio auto è stato aggiunto con successo.');

      onCreate?.(createdGarage);
      await mutateGarages();
    } catch (error) {
      setIsSaving(false);
      addError('Non è stato possibile aggiungere lo spazio auto.');
    }
  }, [addError, addSuccess, mutateGarages, onCreate, propertyId]);

  const editGarage = React.useCallback(async (formValues: UpsertPropertyGarageFormModel) => {
    if (!isEditing) return;

    try {
      setIsSaving(true);

      const garageToUpdate: Garage = {
        ...isEditing.garage,
        type: formValues.type as GarageType,
        adjacent: formValues.adjacent,
        size: formValues.size ? parseFloat(formValues.size) || null : null,
        floor: formValues.floor ? parseFloat(formValues.floor) : null,
      };

      const updatedGarage = await updateGarage(garageToUpdate);

      setIsSaving(false);
      addSuccess('Lo spazio auto è stato modificato con successo.');
      setIsEditing(false);

      onUpdate?.(updatedGarage);
      await mutateGarages();
    } catch (_) {
      setIsSaving(false);
      addError('Non è stato possibile modificare lo spazio auto.');
    }
  }, [addError, addSuccess, isEditing, mutateGarages, onUpdate]);

  const confirmGarageDeletion = React.useCallback(async () => {
    if (!deleteGarageModal.data?.garage) return;

    try {
      setIsSaving(true);

      await deleteGarage(deleteGarageModal.data.garage.id!);

      setIsSaving(false);
      addSuccess('Lo spazio auto è stato rimosso con successo.');
      deleteGarageModal.close();

      onDelete?.(deleteGarageModal.data.garage.id!);
      await mutateGarages();
    } catch (error) {
      setIsSaving(false);
      addError('Non è stato possibile rimuovere lo spazio auto.');
    }
  }, [addError, addSuccess, deleteGarageModal, mutateGarages, onDelete]);

  const getUpdateGarageFormRef = React.useCallback((id: number) => {
    if (!updateGarageFormRefs.current[id]) {
      updateGarageFormRefs.current[id] = React.createRef();
    }
    return updateGarageFormRefs.current[id];
  }, []);

  const submitCreateGarageForm = React.useCallback(() => {
    createGarageFormRef.current?.handleSubmit();
  }, []);

  const submitUpdateGarageForm = React.useCallback((id: number) => {
    if (updateGarageFormRefs.current[id]) {
      updateGarageFormRefs.current[id].current.handleSubmit();
    }
  }, []);

  if (areGaragesLoading && !isMobile) {
    return <SimpleTableSkeleton aria-label="Spazi auto in caricamento" />;
  }

  if (garagesErrors) {
    return (
      <Message
        type="critical"
        message="Non è stato possibile caricare gli spazi auto."
      />
    );
  }

  return (
    <>
      <Stack gap={200} data-testid="manage-property-garages">
        {!isMobile && (
          <SimpleTable aria-label="Tabella spazi auto">
            <SimpleTable.Body>
              {garages?.length === 0 && (
                <SimpleTable.Row>
                  <SimpleTable.Cell>
                    <Message
                      type="neutral"
                      message="Nessuno spazio auto inserito."
                    />
                  </SimpleTable.Cell>
                </SimpleTable.Row>
              )}
              {garages?.map((garage) => (
                <SimpleTable.Row
                  key={garage.id}
                  aria-label={`Informazioni spazio auto ${garage.id}`}
                >
                  {(isEditing && isEditing.garage.id === garage.id) ? (
                    <>
                      <SimpleTable.Cell colSpan={3}>
                        <UpsertPropertyGarageForm
                          loading={isSaving}
                          initialValues={{
                            floor: garage.floor?.toString(),
                            type: garage.type ?? '',
                            adjacent: garage.adjacent ?? false,
                            size: garage.size?.toString(),
                          }}
                          innerRef={getUpdateGarageFormRef(garage.id!)}
                          onSubmit={editGarage}
                        />
                      </SimpleTable.Cell>
                      <SimpleTable.Cell align="right">
                        <HStack>
                          <ActionIcon
                            size="S"
                            color="primary"
                            icon={{ path: ICON_CHECK }}
                            label="Conferma modifiche"
                            aria-label="Conferma modifiche"
                            onClick={() => submitUpdateGarageForm(garage.id!)}
                          />
                          <ActionIcon
                            size="S"
                            icon={{ path: ICON_CLOSE }}
                            label="Annulla modifiche"
                            aria-label="Annulla modifiche"
                            onClick={() => setIsEditing(false)}
                          />
                        </HStack>
                      </SimpleTable.Cell>
                    </>
                  ) : (
                    <>
                      <SimpleTable.Cell colSpan={1}>
                        <Text.BodySmall>
                          {garageTypeLabels[garage.type!]}
                        </Text.BodySmall>
                      </SimpleTable.Cell>

                      <SimpleTable.Cell colSpan={1} aria-label="Dimensione">
                        <Text.BodySmall>
                          {garage.size ? formatSquareMeters(garage.size) : NO_VALUE_SYMBOL}
                        </Text.BodySmall>
                      </SimpleTable.Cell>

                      <SimpleTable.Cell colSpan={1} aria-label="Piano">
                        {garage.type === GarageType.BOX && (
                          <Text.BodySmall>
                            {garage.floor ? `Piano ${formatFloor(garage.floor)}` : NO_VALUE_SYMBOL}
                          </Text.BodySmall>
                        )}
                      </SimpleTable.Cell>

                      <SimpleTable.Cell colSpan={1} aria-label="Pertinenza">
                        <Text.BodySmall>
                          {garage.adjacent ? 'Di pertinenza' : NO_VALUE_SYMBOL}
                        </Text.BodySmall>
                      </SimpleTable.Cell>

                      <SimpleTable.Cell align="right">
                        <HStack>
                          <ActionIcon
                            size="S"
                            icon={{ path: ICON_PENCIL_OUTLINE }}
                            label="Modifica spazio auto"
                            aria-label="Modifica spazio auto"
                            onClick={() => setIsEditing({ garage })}
                          />
                          <ActionIcon
                            size="S"
                            icon={{ path: ICON_TRASH_CAN_OUTLINE }}
                            label="Rimuovi spazio auto"
                            aria-label="Rimuovi spazio auto"
                            onClick={() => deleteGarageModal.open({ garage })}
                          />
                        </HStack>
                      </SimpleTable.Cell>
                    </>
                  )}
                </SimpleTable.Row>
              ))}
              <SimpleTable.Row aria-label="Aggiungi informazioni nuovo spazio auto">
                <SimpleTable.Cell colSpan={4}>
                  <UpsertPropertyGarageForm
                    loading={isSaving}
                    innerRef={createGarageFormRef}
                    onSubmit={(values, actions) => {
                      actions.resetForm();

                      addGarage(values);
                    }}
                  />
                </SimpleTable.Cell>
                <SimpleTable.Cell align="right">
                  <Action
                    size="S"
                    label="Aggiungi"
                    aria-label="Aggiungi spazio auto"
                    onClick={submitCreateGarageForm}
                  />
                </SimpleTable.Cell>
              </SimpleTable.Row>
            </SimpleTable.Body>
          </SimpleTable>
        )}
        {isMobile && (
          <Stack
            gap={150}
            aria-label="Lista card spazi auto"
          >
            {garages?.length === 0 && (
              <Spacing margin={[0, 0, 400]}>
                <Message
                  type="neutral"
                  message="Nessuno spazio auto inserito."
                />
              </Spacing>
            )}
            {garages?.map((garage) => (
              <Card
                key={garage.id}
                aria-label={`Informazioni spazio auto ${garage.id}`}
              >
                <Card.Header
                  title={garageTypeLabels[garage.type!]}
                  primaryActions={
                    !(isEditing && isEditing.garage.id === garage.id) ? [
                      <ActionIcon
                        size="S"
                        icon={{ path: ICON_PENCIL_OUTLINE }}
                        label="Modifica spazio auto"
                        aria-label="Modifica spazio auto"
                        onClick={() => setIsEditing({ garage })}
                      />,
                      <ActionIcon
                        size="S"
                        icon={{ path: ICON_TRASH_CAN_OUTLINE }}
                        label="Rimuovi spazio auto"
                        aria-label="Rimuovi spazio auto"
                        onClick={() => deleteGarageModal.open({ garage })}
                      />,
                    ] : undefined
                  }
                />
                <Card.Content>
                  {(isEditing && isEditing.garage.id === garage.id) ? (
                    <>
                      <UpsertPropertyGarageForm
                        loading={isSaving}
                        initialValues={{
                          floor: garage.floor?.toString(),
                          type: garage.type ?? '',
                          adjacent: garage.adjacent ?? false,
                          size: garage.size?.toString(),
                        }}
                        innerRef={getUpdateGarageFormRef(garage.id!)}
                        onSubmit={editGarage}
                      />
                      <Divider />
                      <Grid gutter={150}>
                        <Grid.Unit size={1 / 2}>
                          <Action
                            expanded
                            size="S"
                            color="primary"
                            label="Salva"
                            aria-label="Conferma modifiche"
                            onClick={() => submitUpdateGarageForm(garage.id!)}
                          />
                        </Grid.Unit>
                        <Grid.Unit size={1 / 2}>
                          <Action
                            expanded
                            size="S"
                            label="Annulla"
                            aria-label="Annulla modifiche"
                            onClick={() => setIsEditing(false)}
                          />
                        </Grid.Unit>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <DetailItemList>
                        <DetailItemList.Item label="Dimensione">
                          {garage.size ? formatSquareMeters(garage.size) : NO_VALUE_SYMBOL}
                        </DetailItemList.Item>
                        <DetailItemList.Item label="Piano">
                          {garage.floor != null ? formatFloor(garage.floor) : NO_VALUE_SYMBOL}
                        </DetailItemList.Item>
                      </DetailItemList>

                      {garage.adjacent && (
                        <Spacing margin={[75, 0, 0]}>
                          <Text.Body>Di pertinenza</Text.Body>
                        </Spacing>
                      )}
                    </>
                  )}
                </Card.Content>
              </Card>
            ))}
            <Card aria-label="Aggiungi informazioni nuovo spazio auto">
              <Card.Header title="Aggiungi nuovo spazio auto" />
              <Card.Content>
                <>
                  <UpsertPropertyGarageForm
                    loading={isSaving}
                    innerRef={createGarageFormRef}
                    onSubmit={(values, actions) => {
                      actions.resetForm();

                      addGarage(values);
                    }}
                  />
                  <Divider />
                  <Action
                    expanded
                    size="S"
                    label="Aggiungi"
                    aria-label="Aggiungi spazio auto"
                    onClick={submitCreateGarageForm}
                  />
                </>
              </Card.Content>
            </Card>
          </Stack>
        )}
      </Stack>
      <Portal>
        {deleteGarageModal.data?.garage && (
          <ConfirmModal
            variant="critical"
            isOpen={deleteGarageModal.isOpen}
            title="Conferma rimozione spazio auto"
            aria-label="Conferma la rimozione dello spazio auto"
            onConfirm={confirmGarageDeletion}
            onAbort={deleteGarageModal.close}
          >
            Confermando l&apos;operazione, lo spazio auto sarà rimosso.
          </ConfirmModal>
        )}
      </Portal>
    </>
  );
};

export default ManagePropertyGarages;
